<template>
  <v-card>
    <v-toolbar flat color="white" density="compact">
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="selected_page"
          :return-object="false"
          item-value="value"
          item-title="text"
          :items="$lodash.sortBy(pages, 'text')"
          hide-details
          single-line>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('page') }}</span>
        </template>
      </v-autocomplete>
      <!--RIGHT PART START (BUTTONS)-->
      <v-spacer/>
      <div class="chip-container">
        <base-action-button v-if="canCreate" @action="createIntroduction">
          <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("create") }}
        </base-action-button>
        <base-action-button v-if="canEdit" @action="save()">
          <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("save") }}
        </base-action-button>
      </div>
    </v-toolbar>
    <v-data-table
        id="base_table_introductions"
        :headers="headers"
        :items="getRecords"
        class="elevation-1"
        :show-select="false"
        :items-per-page="100">
      <template #body="{ items }">
        <draggable :disabled="!canEdit" :list="items" tag="tbody" @end="endDrag(items)" item-key="id">
          <template #item="{ element:introduction }">
            <tr class="sm-normal text-primary">
              <td><PhArrowsVertical size="20" weight="bold" style="cursor: pointer; vertical-align: middle;"/></td>
              <td>{{ introduction.sort_order }}</td>
              <td>{{ $store.getters.translate(introduction.page) }}</td>
              <td><a class="sm-bold" @click="editIntroduction(introduction)">{{ introduction.title }}</a></td>
              <td>{{ introduction.placement }}</td>
              <td><PhCheck v-if="introduction.introduction_active" color="green" size="30"/><PhX v-else color="red" size="30"/></td>
              <!--ACTION BUTTONS-->
              <td style="text-align: center; width: 80px;" v-if="canEdit || canDelete">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn elevation="0" icon v-bind="props">
                      <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item v-if="canEdit" @click="editIntroduction(introduction)">
                      <template #prepend><PhPencilSimpleLine size="24"/></template>
                      <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="canDelete" @click="deleteRecord(introduction.id)">
                      <template #prepend><PhTrash size="24"/></template>
                      <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </draggable>
      </template>
      <template #bottom/>
    </v-data-table>
  </v-card>
  <base-create-dialog modal_name="create_new_record" ref="create_new_record" :page="page" @refresh="load"/>
  <base-edit-dialog modal_name="edit_record" ref="edit_record" :page="page" @refresh="load"/>
</template>

<script>
import { useAbility } from '@casl/vue';
import helpFunctions from "../../plugins/helpFunctions.js";
import draggable from "vuedraggable";
import BaseActionButton from "./BaseActionButton";
import BaseCreateDialog from "./BaseCreateDialog";
import BaseEditDialog from "./BaseEditDialog";
import { PhArrowsVertical, PhCheck, PhDotsThreeVertical, PhFloppyDisk, PhPencilSimpleLine, PhPlus, PhTrash, PhX } from "@phosphor-icons/vue";

export default {
  components: {
    BaseEditDialog,
    BaseCreateDialog,
    draggable,
    BaseActionButton,
    PhCheck, PhX, PhArrowsVertical, PhDotsThreeVertical, PhFloppyDisk, PhPencilSimpleLine, PhPlus, PhTrash
  },
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.introductions,
      all_records: [],
      records: [],
      headers: helpFunctions.headers.introductions,
      pages: [],
      selected_page: "home",
    };
  },
  beforeMount() {
    document.querySelector('#base_table_introductions > .v-table__wrapper > table > tbody')?.remove();
  },
  mounted() {
    const sortableTbody = document.querySelector('#base_table_introductions > .v-table__wrapper > table > tbody > tbody');
    // <draggable> ruin view in <tbody> (create a <div> tag inside <tbody>)
    //FIX: https://github.com/SortableJS/Vue.Draggable/issues/1212#issuecomment-1918880591
    if (sortableTbody) {
      const tbody = document.querySelector('#base_table_introductions > .v-table__wrapper > table > tbody')
      tbody.parentNode.append(sortableTbody);
      tbody.remove()
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      let response = await helpFunctions.getModelsForTables(this.$toast, this.page.name);
      this.all_records = this.$lodash.sortBy(response.data, 'sort_order');
      if(this.pages.length === 0) {
        this.fillPagesDropdown();
      }
      this.showRecords();
    },
    fillPagesDropdown() {
      let all_pages = ["home","actionanddecisions","actionanddecisions_single","actionanddecisionitems","dnvverificationregisters","dnvverificationregisters_single","projects","projects_single","projectmanagement","projectmanagement_single","punchlists","punchlists_single","punchlistitems","platforms","documents"];
      all_pages.forEach((page) => {
        this.pages.push({value: page, text: this.$store.getters.translate(page)});
      });
    },
    showRecords() {
      this.records = [];
      for (let i = 0; i < this.all_records.length; i++) {
        if (this.all_records[i].page === this.selected_page) {
          this.records.push(this.all_records[i]);
        }
      }
    },
    save() {
      for (let i = 0; i < this.records.length; i++) {
        helpFunctions.saveModelSilently(this.page.name, this.records[i]);
      }
      this.$toast.success(this.$store.getters.translate("successfully_saved"));
    },
    endDrag(items) {
      let temp_records = this.records;
      for (let i = 0; i < temp_records.length; i++) {
        for (let z = 0; z < items.length; z++) {
          if (temp_records[i].id === items[z].id && temp_records[i].sort_order !== z + 1) {
            this.records[i].sort_order = z + 1;
          }
        }
      }
      this.records.sort((a, b) => a.sort_order - b.sort_order); // The table was not re-sorted in the previous algorithm, although the values were changed correctly.
    },
    createIntroduction() {
      this.$refs.create_new_record.load();
      this.$refs.create_new_record.record.page = this.selected_page;
      this.$refs.create_new_record.record.placement = "auto";
    },
    editIntroduction(item) {
      this.$refs.edit_record.load(item.id);
    },
    async deleteRecord(id) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async res => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, id);
          await this.load();
        }
      });
    }
  },
  computed: {
    getRecords() {
      if(this.records) {
        return this.records;
      }
      else {
        return [];
      }
    },
    canCreate() {
      return this.can("create", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
  },
  watch: {
    selected_page: {
      handler() {
        this.showRecords();
      },
    },
  },
};
</script>

<style scoped>
.v-checkbox {
  display: flex;
}

.chip-container {
  display: flex;
  gap: 8px;
  margin-right: 16px;
}
</style>